import React, { useState } from "react";
import { Alert, Badge, Button, Form, Table } from "react-bootstrap";
import { AlertTriangle, ExternalLink, X } from "react-feather";
import { Link } from "react-router";
import { formatDistanceToNow } from "date-fns";
import { apiErrorMessage, useGet, usePost } from "../../api";
import {
  COUPON_DOMAIN_SOURCE_LABELS,
  COUPON_ISSUE_EXPLAINERS,
  COUPON_ISSUE_LABELS,
  CouponIssueType,
  ICouponIssue,
} from "../../interfaces";

const TYPE_VARIANT: Record<CouponIssueType, string> = {
  [CouponIssueType.NeedsSuspension]: "danger",
  [CouponIssueType.FailingForShoppers]: "danger",
  [CouponIssueType.NeedsActivation]: "info",
  [CouponIssueType.CouponBoxNotFound]: "warning",
};

const evidence = (i: ICouponIssue): string => {
  switch (i.type) {
    case CouponIssueType.NeedsSuspension:
      return `Rejected in ${i.failures} test${i.failures === 1 ? "" : "s"}`;
    case CouponIssueType.NeedsActivation:
      return `Not rejected in ${i.attempts} test${i.attempts === 1 ? "" : "s"}`;
    case CouponIssueType.FailingForShoppers:
      return `${i.failures} of ${i.attempts} shopper attempts failed, none applied`;
    case CouponIssueType.CouponBoxNotFound:
      return `${i.failures} shopper attempt${i.failures === 1 ? "" : "s"} · codes ${i.sampleCodes.join(", ")}`;
  }
};

/** Open coupon issues for one board row, each one closable as "Not an issue". */
export const CouponIssuesPanel: React.FC<{
  itemId: string;
  onChanged: () => void;
}> = ({ itemId, onChanged }) => {
  const { data, refetch } = useGet<{ results: ICouponIssue[] }>(
    `work-pipeline/${itemId}/coupon-issues`
  );
  const dismissReq = usePost<{ success: boolean }, { reason: string }>();
  const [dismissing, setDismissing] = useState<string | null>(null);
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  const issues = data?.results ?? [];
  if (!issues.length) return null;

  const dismiss = async (issueId: string) => {
    setError(null);
    try {
      await dismissReq.execute(
        `work-pipeline/${itemId}/coupon-issues/${issueId}/dismiss`,
        { reason: reason.trim() }
      );
      setDismissing(null);
      setReason("");
      await refetch();
      onChanged();
    } catch (err) {
      setError(apiErrorMessage(err, "Couldn't mark it as not an issue."));
    }
  };

  return (
    <Alert variant="light" className="border py-2">
      <div className="d-flex align-items-center gap-2 mb-2">
        <AlertTriangle size={14} className="text-danger" />
        <strong className="small">
          {issues.length} coupon issue{issues.length === 1 ? "" : "s"} raised
          automatically
        </strong>
        <small className="text-muted">
          Each one clears itself once the code is suspended or re-enabled, or
          once the template is marked fixed.
        </small>
      </div>
      {error && (
        <Alert
          variant="danger"
          className="py-1 small"
          dismissible
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}
      <Table size="sm" className="mb-0 small align-middle">
        <thead>
          <tr>
            <th>Issue</th>
            <th>Code</th>
            <th>Evidence</th>
            <th>What the shop said</th>
            <th>Last seen</th>
            <th title="Where this domain came from">Domain from</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {issues.map((i) => (
            <React.Fragment key={i._id}>
              <tr>
                <td>
                  <Badge
                    bg={TYPE_VARIANT[i.type]}
                    title={COUPON_ISSUE_EXPLAINERS[i.type]}
                  >
                    {COUPON_ISSUE_LABELS[i.type]}
                  </Badge>
                </td>
                <td className="font-monospace">
                  {i.couponId ? (
                    <Link
                      to={`/kindred/coupons/${i.couponId}`}
                      target="_blank"
                      title="Open the coupon to suspend or re-enable it"
                    >
                      {i.code} <ExternalLink size={11} />
                    </Link>
                  ) : (
                    i.code || "—"
                  )}
                </td>
                <td>{evidence(i)}</td>
                <td
                  className="text-muted text-truncate"
                  style={{ maxWidth: 260 }}
                  title={i.lastError ?? ""}
                >
                  {i.lastError || "—"}
                </td>
                <td className="text-nowrap text-muted">
                  {formatDistanceToNow(new Date(i.lastSeenAt), {
                    addSuffix: true,
                  })}
                </td>
                <td className="text-muted">
                  {COUPON_DOMAIN_SOURCE_LABELS[i.domainSource]}
                </td>
                <td className="text-end">
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    title="Close this issue with a reason. It stays closed unless something new happens."
                    onClick={() => {
                      setDismissing(dismissing === i._id ? null : i._id);
                      setReason("");
                    }}
                  >
                    <X size={12} className="me-1" />
                    Not an issue
                  </Button>
                </td>
              </tr>
              {dismissing === i._id && (
                <tr>
                  <td colSpan={7}>
                    <div className="d-flex gap-2">
                      <Form.Control
                        size="sm"
                        autoFocus
                        placeholder="Why isn't this an issue? e.g. code works, merchant was down"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                      />
                      <Button
                        size="sm"
                        variant="secondary"
                        disabled={reason.trim().length < 3 || dismissReq.loading}
                        onClick={() => dismiss(i._id)}
                        title="Save the reason and close the issue"
                      >
                        Save
                      </Button>
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </Table>
    </Alert>
  );
};
